const API_BASE_URL = '/api';

const getAuthToken = () => {
  return localStorage.getItem('token') || localStorage.getItem('access_token');
};

const api = {
  // ─── Auth ───────────────────────────────────────────────────────────────────
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) { const errData = await response.json(); const msg = errData.message || errData.error || 'Login failed'; throw new Error(msg.includes('Field validation') ? 'Invalid email or password. Please check your credentials.' : msg); }
    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('token', data.access_token);
    }
    return data;
  },

  // ─── Trader ─────────────────────────────────────────────────────────────────
  getAccounts: async () => {
    const response = await fetch(`${API_BASE_URL}/trader/accounts`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch accounts');
    const data = await response.json();
    return data.accounts || data;
  },

  getAlphaRank: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/trader/dashboard-db?account_id=${accountId}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch AlphaRank');
    return response.json();
  },

  getPlanAlphaRank: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/trader/plan-alpharank?account_id=${accountId}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch Plan AlphaRank');
    return response.json();
  },
  getAlphaRankPerPair: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/trader/alpharank-perpair?account_id=${accountId}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch per-pair AlphaRank');
    return response.json();
  },

  getDetailedAlphaRank: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/trader/dashboard-db?account_id=${accountId}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch detailed AlphaRank');
    return response.json();
  },

  getTrades: async (accountId, limit = 50) => {
    const response = await fetch(`${API_BASE_URL}/trader/trades?account_id=${accountId}&limit=${limit}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch trades');
    return response.json();
  },

  getMonthlyPerformance: async (accountId) => {
    const response = await fetch(`${API_BASE_URL}/trader/monthly-performance?account_id=${accountId}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch monthly performance');
    return response.json();
  },

  getEquityCurve: async (accountId, days = 30) => {
    const response = await fetch(`${API_BASE_URL}/trader/equity-curve?account_id=${accountId}&days=${days}`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch equity curve');
    return response.json();
  },

  // ─── Investor ────────────────────────────────────────────────────────────────
  getInvestorPortfolio: async () => {
    const response = await fetch(`${API_BASE_URL}/investor/portfolio`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch investor portfolio');
    return response.json();
  },

  getInvestorAllocations: async () => {
    const response = await fetch(`${API_BASE_URL}/investor/allocations`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch investor allocations');
    return response.json();
  },

  updateInvestorAllocation: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/investor/allocations`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to update allocation');
    return response.json();
  },

  getInvestorSubscriptions: async () => {
    const response = await fetch(`${API_BASE_URL}/investor/subscriptions`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch subscriptions');
    return response.json();
  },

  subscribeToAnalyst: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/investor/subscribe`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to subscribe');
    return response.json();
  },

  // ─── Analyst ─────────────────────────────────────────────────────────────────
  getAnalystDashboard: async () => {
    const response = await fetch(`${API_BASE_URL}/analyst/dashboard`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch analyst dashboard');
    return response.json();
  },

  getAnalystSignalSets: async () => {
    const response = await fetch(`${API_BASE_URL}/analyst/signal-sets`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch signal sets');
    return response.json();
  },

  createAnalystSignalSet: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signal-sets`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d1 = await response.json();
    if (!response.ok) throw new Error(d1.error || 'Failed to create signal set');
    return d1;
  },

  updateAnalystSignalSet: async (setId, payload) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signal-sets/${setId}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to update signal set');
    return response.json();
  },

  deleteAnalystSignalSet: async (setId) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signal-sets/${setId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await response.json();
    if (!response.ok) throw new Error(d.error || 'Failed to delete signal set');
    return d;
  },

  getAnalystSignals: async (signalSetId) => {
    const url = signalSetId
      ? `${API_BASE_URL}/analyst/signals?signal_set_id=${signalSetId}`
      : `${API_BASE_URL}/analyst/signals`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch signals');
    return response.json();
  },

  createAnalystSignal: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signals`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d2 = await response.json();
    if (!response.ok) throw new Error(d2.error || 'Failed to create signal');
    return d2;
  },

  updateAnalystSignal: async (signalId, payload) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signals/${signalId}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to update signal');
    return response.json();
  },

  cancelAnalystSignal: async (signalId) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signals/${signalId}/cancel`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to cancel signal');
    return response.json();
  },

  getAnalystPerformance: async () => {
    const response = await fetch(`${API_BASE_URL}/analyst/performance`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch performance');
    return response.json();
  },
  getAnalystVirtualAlphaRank: async (setId, riskPct=1.5) => {
    const res = await fetch(`/api/analyst/virtual-alpharank?set_id=${setId}&risk_pct=${riskPct}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')||localStorage.getItem('token')||''}` }
    });
    if (!res.ok) throw new Error('Failed to fetch virtual alpharank');
    return res.json();
  },
  getAnalystAlphaRank: async (setId) => {
    const url = setId && setId!=="all" ? `/api/analyst/alpharank?setId=${setId}` : `/api/analyst/alpharank`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch analyst alpharank');
    return response.json();
  },
  listSignalSyncEligibleAccounts: async () => {
    const response = await fetch(`${API_BASE_URL}/analyst/signalsync/accounts`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch eligible accounts');
    return response.json();
  },
  activateSignalSync: async (setId, accountId) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signalsync/activate`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ set_id: setId, account_id: accountId }),
    });
    const data = await response.json();
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to activate SignalSync');
    return data;
  },
  deactivateSignalSync: async (setId) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signalsync/deactivate`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ set_id: setId }),
    });
    const data = await response.json();
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to deactivate SignalSync');
    return data;
  },
  createSignalSyncAccount: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/analyst/signalsync/create-account`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || !data.ok) throw new Error(data.error || 'Failed to create account');
    return data;
  },
  listMySignalSyncAccounts: async () => {
    const response = await fetch(`${API_BASE_URL}/analyst/signalsync/my-accounts`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch my SignalSync accounts');
    return response.json();
  },
  // ─── Analyst Self-Execution ─────────────────────────────────────────────────
  getSelfExecutionStatus: async () => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/status`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to load execution account status');
    return d;
  },

  getTelegramSettings: async () => {
    const r = await fetch(`${API_BASE_URL}/analyst/telegram/settings`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to load Telegram settings');
    return d;
  },

  updateTelegramSettings: async (payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/telegram/settings`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to save Telegram settings');
    return d;
  },
  getNotificationChannels: async () => {
    const r = await fetch(`${API_BASE_URL}/analyst/notification-channels`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to load notification channels');
    return d;
  },
  addNotificationChannel: async (payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/notification-channels`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to add notification channel');
    return d;
  },
  deleteNotificationChannel: async (id) => {
    const r = await fetch(`${API_BASE_URL}/analyst/notification-channels/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to delete notification channel');
    return d;
  },
  testNotificationChannel: async (id) => {
    const r = await fetch(`${API_BASE_URL}/analyst/notification-channels/${id}/test`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to send test message');
    return d;
  },
  createSelfExecutionAccount: async (payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/account`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to create execution account');
    return d;
  },
  generateSelfExecutionEAKey: async (payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/ea-key`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to generate EA key');
    return d;
  },
  linkSelfExecutionAccount: async (payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/link`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to link execution account');
    return d;
  },
  unlinkSelfExecutionAccount: async (linkId) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/unlink/${linkId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to unlink execution account');
    return d;
  },
  setSelfExecutionRiskLevel: async (payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/risk-level`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to set risk level');
    return d;
  },
  updateSelfExecutionAccount: async (accountId, payload) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/account/${accountId}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to update account');
    return d;
  },
  deleteSelfExecutionAccount: async (accountId) => {
    const r = await fetch(`${API_BASE_URL}/analyst/self-execution/account/${accountId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Failed to delete account');
    return d;
  },
};

export default api;

export const adminAPI = {
  getStats: async () => {
    const r = await fetch('/api/admin/stats', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch stats');
    return r.json();
  },
  getUsers: async () => {
    const r = await fetch('/api/admin/users', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch users');
    return r.json();
  },
  updateUser: async (id, data) => {
    const r = await fetch(`/api/admin/users/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to update user');
    return r.json();
  },
  deleteUser: async (id) => {
    const r = await fetch(`/api/admin/users/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to delete user');
    return r.json();
  },
  getTradingAccounts: async () => {
    const r = await fetch('/api/admin/trading-accounts', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch trading accounts');
    return r.json();
  },
  getSignalSets: async () => {
    const r = await fetch('/api/admin/signal-sets', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch signal sets');
    return r.json();
  },
  getBrokers: async () => {
    const r = await fetch('/api/admin/brokers', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch brokers');
    return r.json();
  },
  createBroker: async (data) => {
    const r = await fetch('/api/admin/brokers', { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to create broker');
    return r.json();
  },
  updateBroker: async (id, data) => {
    const r = await fetch(`/api/admin/brokers/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to update broker');
    return r.json();
  },
  deleteBroker: async (id) => {
    const r = await fetch(`/api/admin/brokers/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to delete broker');
    return r.json();
  },
  getFeeOverrides: async () => {
    const r = await fetch('/api/admin/fee-overrides', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch fee overrides');
    return r.json();
  },
  upsertFeeOverride: async (data) => {
    const r = await fetch('/api/admin/fee-overrides', { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to save fee override');
    return r.json();
  },
  deleteFeeOverride: async (id) => {
    const r = await fetch(`/api/admin/fee-overrides/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to delete fee override');
    return r.json();
  },
  getFeeRules: async () => {
    const r = await fetch('/api/admin/fee-rules', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch fee rules');
    return r.json();
  },
  // ─── Partner Admin (self-service, /api/partner-admin/*) ──────────────────
  getPartnerAdminConfig: async () => {
    const r = await fetch('/api/partner-admin/config', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  updatePartnerAdminConfig: async (data) => {
    const r = await fetch('/api/partner-admin/config', { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    return r.json();
  },
  getPartnerAdminProviders: async () => {
    const r = await fetch('/api/partner-admin/providers', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  setPartnerAdminProviderApproval: async (id, approved) => {
    const r = await fetch(`/api/partner-admin/providers/${id}/approval`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ approved }) });
    return r.json();
  },
  getPartnerAdminFeeRules: async () => {
    const r = await fetch('/api/partner-admin/fee-rules', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  updatePartnerAdminFeeRule: async (feeTypeId, isEnabled, params) => {
    const r = await fetch('/api/partner-admin/fee-rules', { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ fee_type_id: feeTypeId, is_enabled: isEnabled, params }) });
    return r.json();
  },
  partnerAdminWithdrawalAction: async (id, action, note) => {
    const r = await fetch(`/api/partner-admin/withdrawals/${id}/${action}`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ adminNote: note || '' }) });
    return r.json();
  },
  partnerAdminInvoiceAction: async (id, action, text) => {
    const body = action === 'reject' ? { reason: text || '' } : { notes: text || '' };
    const r = await fetch(`/api/partner-admin/invoices/${id}/${action}`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return r.json();
  },
  partnerAdminCreateAccount: async (body) => {
    const r = await fetch('/api/partner-admin/accounts', { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return r.json();
  },
  partnerAdminSetAccountCurrency: async (id, currency) => {
    const r = await fetch(`/api/partner-admin/accounts/${id}/currency`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ currency }) });
    return r.json();
  },
  partnerAdminDisableAccount: async (id) => {
    const r = await fetch(`/api/partner-admin/accounts/${id}/disable`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: '{}' });
    return r.json();
  },
  getPartnerAdminAffiliates: async () => {
    const r = await fetch('/api/partner-admin/affiliates', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminWithdrawals: async (status) => {
    const p = new URLSearchParams();
    if (status) p.append('status', status);
    const r = await fetch(`/api/partner-admin/withdrawals?${p}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminInvoices: async (status) => {
    const p = new URLSearchParams();
    if (status) p.append('status', status);
    const r = await fetch(`/api/partner-admin/invoices?${p}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminFeeReport: async (from, to) => {
    const p = new URLSearchParams();
    if (from) p.append('from', from);
    if (to) p.append('to', to);
    const r = await fetch(`/api/partner-admin/fee-report?${p}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminSignalSets: async () => {
    const r = await fetch('/api/partner-admin/signal-sets', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminAccounts: async () => {
    const r = await fetch('/api/partner-admin/accounts', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminOverview: async () => {
    const r = await fetch('/api/partner-admin/overview', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  getPartnerAdminUsers: async () => {
    const r = await fetch('/api/partner-admin/users', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.json();
  },
  updateFeeRule: async (id, data) => {
    const r = await fetch(`/api/admin/fee-rules/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to update fee rule');
    return r.json();
  },
  getAuditLogs: async (filters={}) => {
    const params = new URLSearchParams();
    if (filters.event_type) params.append('event_type', filters.event_type);
    if (filters.status) params.append('status', filters.status);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    params.append('limit', filters.limit || 200);
    const r = await fetch(`/api/admin/audit-logs?${params}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch audit logs');
    return r.json();
  },
  getCashflowSummary: async () => {
    const r = await fetch('/api/admin/cashflow/summary', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch cashflow summary');
    return r.json();
  },
  getSignalSubscriptions: async () => {
    const r = await fetch('/api/admin/cashflow/signal-subscriptions', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch signal subscriptions');
    return r.json();
  },
  getCopySubscriptions: async () => {
    const r = await fetch('/api/admin/cashflow/copy-subscriptions', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch copy subscriptions');
    return r.json();
  },
  getUserGrowth: async () => {
    const r = await fetch('/api/admin/cashflow/user-growth', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch user growth');
    return r.json();
  },
  getFeeConfig: async () => {
    const r = await fetch('/api/admin/config/fees', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch fee config');
    return r.json();
  },
  updateFeeConfig: async (key, data) => {
    const r = await fetch(`/api/admin/config/fees/${key}`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to update fee config');
    return r.json();
  },
  getAffiliateTiers: async () => {
    const r = await fetch('/api/admin/config/affiliate-tiers', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch affiliate tiers');
    return r.json();
  },
  updateAffiliateTier: async (id, data) => {
    const r = await fetch(`/api/admin/config/affiliate-tiers/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!r.ok) throw new Error('Failed to update affiliate tier');
    return r.json();
  },
  getConfigDocs: async () => {
    const r = await fetch('/api/admin/config/docs', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch docs');
    return r.json();
  },
  getFeeReport: async (from, to) => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const r = await fetch(`/api/admin/fee-simulation/all?${params}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch fee report');
    return r.json();
  },
  getFeeReportAccount: async (accountId, from, to) => {
    const params = new URLSearchParams({ account_id: accountId });
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const r = await fetch(`/api/admin/fee-simulation?${params}`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to fetch account fee report');
    return r.json();
  },
  updateIBStatus: async (accountId, usesPlatformIB, ibBrokerId) => {
    const r = await fetch(`/api/admin/trading-accounts/${accountId}/ib-status`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ uses_platform_ib: usesPlatformIB, ib_broker_id: ibBrokerId || null })
    });
    if (!r.ok) throw new Error('Failed to update IB status');
    return r.json();
  },
  recalcAlphaRank: async () => {
    const r = await fetch('/api/admin/recalc-alpharank', { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!r.ok) throw new Error('Failed to recalc');
    return r.json();
  },
  createUser: async (data) => {
    const r = await fetch('/api/admin/users', { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
  forceVerifyEmail: async (id) => {
    const r = await fetch(`/api/admin/users/${id}/verify`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
  resetPassword: async (id, password) => {
    const r = await fetch(`/api/admin/users/${id}/reset-password`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
  suspendUser: async (id, suspend) => {
    const r = await fetch(`/api/admin/users/${id}/suspend`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ suspend }) });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
  impersonateUser: async (id) => {
    const r = await fetch(`/api/admin/users/${id}/impersonate`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
  updateAccountCurrency: async (id, currency) => {
    const r = await fetch(`/api/admin/trading-accounts/${id}/currency`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ currency })
    });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
  deleteTradingAccount: async (id) => {
    const r = await fetch(`/api/admin/trading-accounts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
    return r.ok ? r.json() : Promise.reject(new Error(await r.text()));
  },
}; 
